import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterX } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { CampoData } from '../../componentes/formulario';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { dinheiro } from '../../nucleo/formato';

interface LinhaCentro {
  id: string;
  centro_custo_id: string | null;
  centro_custo_nome: string | null;
  aprovadas_centavos: number;
  reembolsadas_centavos: number;
  despesas: number;
}

/**
 * r64 SPEC-B5: relatório por centro de custo (spec e ficha das Despesas): aprovadas (a reembolsar) e reembolsadas no período,
 * pela data do gasto (`GET /despesas/por-centro`). Sem período, o mês atual até hoje (o padrão da API). Mesmos componentes da
 * Auditoria (tabela com o período nos filtros).
 */
export function PorCentro() {
  const { t } = useTranslation('despesas');
  const [filtros, setFiltros] = useState({ de: '', ate: '' });
  const mudar = (k: keyof typeof filtros, v: string) => setFiltros((f) => ({ ...f, [k]: v }));
  const colunas: Coluna<LinhaCentro>[] = [
    {
      chave: 'centro_custo_nome',
      rotulo: t('centros.centro'),
      principal: true,
      render: (l) => <span className="font-medium">{l.centro_custo_id ? (l.centro_custo_nome ?? t('semAcesso')) : t('centros.semCentro')}</span>,
    },
    { chave: 'despesas', rotulo: t('campos.despesas'), alinhar: 'direita', mono: true },
    { chave: 'aprovadas_centavos', rotulo: t('centros.aprovadas'), alinhar: 'direita', mono: true, render: (l) => dinheiro(l.aprovadas_centavos) },
    { chave: 'reembolsadas_centavos', rotulo: t('centros.reembolsadas'), alinhar: 'direita', mono: true, render: (l) => dinheiro(l.reembolsadas_centavos) },
  ];
  return (
    <>
      <p className="mb-3 text-sm text-tinta-3">{t('centros.ajuda')}</p>
      <Tabela<LinhaCentro>
        tela="despesas.centros"
        caminho="/despesas/por-centro"
        chaveConsulta={['despesas', 'por-centro']}
        params={{ de: filtros.de || undefined, ate: filtros.ate || undefined }}
        filtros={filtros}
        aoAplicarFiltros={(f) => setFiltros({ de: String(f.de ?? ''), ate: String(f.ate ?? '') })}
        colunas={colunas}
        vazio={<Vazio texto={t('vazio.centros')} />}
        filtrosExtra={
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-sm text-tinta-3">
              {t('centros.de')}
              <CampoData type="date" className="w-36" value={filtros.de} max={filtros.ate || undefined} onChange={(e) => mudar('de', e.target.value)} />
            </label>
            <label className="flex items-center gap-1.5 text-sm text-tinta-3">
              {t('centros.ate')}
              <CampoData type="date" className="w-36" value={filtros.ate} min={filtros.de || undefined} onChange={(e) => mudar('ate', e.target.value)} />
            </label>
            {(filtros.de || filtros.ate) && (
              <Botao variante="fantasma" tamanho="sm" icone={<FilterX className="size-4" />} onClick={() => setFiltros({ de: '', ate: '' })}>
                {t('centros.limpar')}
              </Botao>
            )}
          </div>
        }
      />
    </>
  );
}
